import type { RoomOccupancy, RoomsParticipantDistribution } from '@/domain/shared/types'
import type { SearchState } from '@/scopes/search/search/infrastructure/store/store'
import { normalizeDate } from '@sunwebgroup/ui-components'
import { sub } from 'date-fns'
import { always, isEmpty, uniq } from 'ramda'
import { CHILDREN_MAX_AGE, CHILDREN_MIN_AGE } from '@/domain/shared/constants'
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { DEFAULT_RESULTS_PER_PAGE } from '@/scopes/search/search/domain/constants'
import { getPassThroughParams } from '@/scopes/search/search/domain/getPassThroughParams'
import { VALID_SORT_OPTIONS } from '@/scopes/search/search/domain/sorting'
import {
  FiltersSelection,
  MultiSelectKeys,
  SearchFilters,
  SearchFilterSelection,
  SearchFilterType,
  SearchMultiSelectFilters,
} from '@/scopes/search/search/infrastructure/api/types'
import { searchPassThroughParams, type SearchPassThroughParams } from '@/scopes/search/types'

export interface DeserializedSearchQueryString {
  limit?: string
  offset?: string
  sort?: string
  Lastminute?: string
  Stars?: string
  MetersToBeach?: string
  MetersToCenter?: string
  RoomCount?: string
  TransportType?: string | string[]
  AccommodationType?: string[]
  Airline?: string[]
  ArrivalAirport?: string[]
  Campaigns?: string[]
  ChildrenFacilities?: string[]
  City?: string[]
  Country?: string[]
  Collection?: string[]
  DepartureDate?: string[]
  DepartureAirport?: string[]
  Discount?: string[]
  Duration?: string[]
  Facilities?: string[]
  InternetFacilities?: string[]
  Mealplan?: string[]
  Region?: string[]
  Themes?: string[]
  PricePerPerson?: string[]
  CustomersRating?: string
  Participants?: string[][]
  CampaignsAndDiscounts?: string[]
  HasAvailability?: string
  HasAvailabilityNextSeason?: string
  MetersToSlope?: string
  MetersToSkiLift?: string
  SkiAreaMinimumAltitudeMeters?: string
  BedsInLivingroom?: string
  BathroomCount?: string
  SkiAreaSlopeKms?: string
  IsFlightOnly?: string
  accoIds?: string | string[]
}

export type DeserializedQueryStringToStateMapper = (
  deserializedQueryString: DeserializedSearchQueryString,
  passThroughParams?: Partial<SearchPassThroughParams>
) => Partial<SearchState>

export type LowercaseKeys<T> = {
  [K in keyof T as K extends string ? Lowercase<K> : K]: T[K];
}

interface Dependencies {
  queryStringSerializer: QueryStringSerializer
  mappers: DeserializedQueryStringToStateMapper[]
  defaultValues: Partial<FiltersSelection<SearchFilters>>
}

function lowercaseKeys(obj: Record<string, string | string[]>): Record<string, string | string[]> {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key]
    if (Array.isArray(value)) {
      const currentAccKeyValue = (acc[key.toLowerCase()] as string[]) || []
      const filteredNewValue = value.filter((v: string) => v !== '')
      return { ...acc, [key.toLowerCase()]: [...currentAccKeyValue, ...filteredNewValue] }
    }
    return { ...acc, [key.toLowerCase()]: obj[key] }
  }, {} as Record<string, string | string[]>)
}

export function paginationMapper({ offset, limit }: LowercaseKeys<DeserializedSearchQueryString>): Partial<SearchState> {
  if (!limit && !offset)
    return {}

  const parsedOffset = offset ? Number.parseInt(offset, 10) : 0
  const parsedLimit = limit ? Number.parseInt(limit, 10) : DEFAULT_RESULTS_PER_PAGE

  return {
    pagination: {
      offset: parsedOffset,
      limit: parsedLimit,
    },
  }
}

export function sortingMapper({ sort }: LowercaseKeys<DeserializedSearchQueryString>): Partial<SearchState> {
  const sortValue = VALID_SORT_OPTIONS.find((option: string) => option.toLowerCase() === sort?.toLowerCase())
  if (!sort || !sortValue)
    return {}

  return {
    sorting: {
      by: sortValue,
    },
  }
}

const ACCOMMODATION_ID_PATTERN = /^[1-9]\d*$/

function isValidAccommodationId(accommodationId: string) {
  return ACCOMMODATION_ID_PATTERN.test(accommodationId.trim())
}

export function accommodationIdsMapper({ accoids }: LowercaseKeys<DeserializedSearchQueryString>): Partial<SearchState> {
  if (!accoids)
    return {}

  const rawAccommodationIds = Array.isArray(accoids) ? accoids : [accoids]
  const accommodationIds = uniq(
    rawAccommodationIds
      .filter(isValidAccommodationId)
      .map(accommodationId => accommodationId.trim()),
  )

  if (isEmpty(accommodationIds))
    return {}

  return { initialAccommodationIds: accommodationIds }
}

export function combineFilterSelectionMappers(...mappers: DeserializedQueryStringToStateMapper[]): DeserializedQueryStringToStateMapper {
  if (isEmpty(mappers))
    return always({})

  return (deserializedQueryString, passThroughParams) => ({
    filterSelection: mappers.reduce<Partial<SearchFilterSelection>>(
      (acc, mapper) => ({
        ...acc,
        ...mapper(deserializedQueryString, passThroughParams).filterSelection,
      }),
      {},
    ),
  })
}

export function identityFilterSelectionArrayMapper<K extends SearchFilterType>(filterType: K, defaultValue?: unknown): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
    const lowerCasedFilterType = filterType.toLowerCase() as keyof LowercaseKeys<DeserializedSearchQueryString>
    let filterSelectedValue = deserializedQueryString?.[lowerCasedFilterType] ?? defaultValue
    if (filterSelectedValue === undefined)
      return { filterSelection: {} }

    // Always wrap in array for MultiSelect filters
    if (isMultiSelectFilterKey(filterType)) {
      if (!Array.isArray(filterSelectedValue)) {
        filterSelectedValue = [filterSelectedValue]
      }
      // Remove undefined/null/empty string values
      filterSelectedValue = (filterSelectedValue as unknown[]).filter(v => v != null && v !== '')
      // Uppercase all string values
      filterSelectedValue = (filterSelectedValue as string[]).map((option: string) => option.toUpperCase())
    }
    else if (typeof filterSelectedValue === 'string') {
      filterSelectedValue = filterSelectedValue.toUpperCase()
    }

    return {
      filterSelection: {
        [filterType]: filterSelectedValue,
      },
    }
  }
}

export function identityFilterSelectionMapper<K extends SearchFilterType>(filterType: K, defaultValue?: unknown): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
    const lowerCasedFilterType = filterType.toLowerCase() as keyof LowercaseKeys<DeserializedSearchQueryString>
    const filterSelectedValue = deserializedQueryString?.[lowerCasedFilterType] ?? defaultValue
    if (filterSelectedValue === undefined)
      return { filterSelection: {} }
    return {
      filterSelection: {
        [filterType]: filterSelectedValue,
      },
    }
  }
}

export const lastMinuteMapper: DeserializedQueryStringToStateMapper = (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
  if (!deserializedQueryString?.lastminute)
    return { filterSelection: {} }
  const { lastminute } = deserializedQueryString

  return {
    filterSelection: {
      Lastminute: lastminute?.toLowerCase(),
    },
  }
}

export const collectionMapper: DeserializedQueryStringToStateMapper = (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
  if (!deserializedQueryString?.collection)
    return { filterSelection: {} }
  const { collection } = deserializedQueryString
  const collectionArray = Array.isArray(collection) ? collection : [collection]
  const collectionValue = collectionArray.map((value: string) => value.toLowerCase())

  return {
    filterSelection: {
      Collection: collectionValue,
    },
  }
}

export const pricePerPersonMapper: DeserializedQueryStringToStateMapper = (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
  if (!deserializedQueryString?.priceperperson)
    return { filterSelection: {} }
  const { priceperperson } = deserializedQueryString

  if (priceperperson[1] === '999999') {
    return {
      filterSelection: {
        PricePerPerson: [Number.parseInt(priceperperson[0], 10), null],
      },
    }
  }

  return {
    filterSelection: {
      PricePerPerson: [Number.parseInt(priceperperson[0], 10), Number.parseInt(priceperperson[1], 10)],
    },
  }
}

export const departureDateMapper: DeserializedQueryStringToStateMapper = (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
  if (!deserializedQueryString?.departuredate)
    return { filterSelection: {} }
  const { departuredate } = deserializedQueryString
  const departureDateArray = (Array.isArray(departuredate) ? departuredate : [departuredate]).filter(Boolean)
  if (!departureDateArray.length)
    return { filterSelection: {} }
  const normalized = departureDateArray.map(normalizeDate)
  const value = normalized.length === 1 ? normalized[0] : normalized

  return {
    filterSelection: {
      DepartureDate: value,
    },
  }
}

export function transportTypeMapper(defaultValue: string): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
    if (!deserializedQueryString?.transporttype) {
      return {
        filterSelection: {
          TransportType: defaultValue,
        },
      }
    }
    const { transporttype } = deserializedQueryString
    const value = transporttype.length === 1 ? transporttype[0] : transporttype
    const transportTypeOptions = ['Flight', 'Bus', 'SelfDrive']
    const selectedTransportType = transportTypeOptions.find((option: string) => option.toLowerCase() === (value as string).toLowerCase())

    return {
      filterSelection: {
        TransportType: selectedTransportType || defaultValue,
      },
    }
  }
}

export function durationMapper(defaultValue: string[] | undefined): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>, passThroughParams?: Partial<SearchPassThroughParams>) => {
    const filterSelectedValue
      = deserializedQueryString?.duration ?? (passThroughParams?.autoLoad === 'false' ? undefined : defaultValue || [])
    if (filterSelectedValue === undefined)
      return { filterSelection: {} }

    return {
      filterSelection: {
        Duration: filterSelectedValue,
      },
    }
  }
}

export function participantsMapper(defaultValue: RoomsParticipantDistribution | undefined): DeserializedQueryStringToStateMapper {
  return (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
    if (!deserializedQueryString?.participants) {
      return {
        filterSelection: {
          Participants: defaultValue || [
            {
              adults: 2,
              children: [],
              babies: [],
            },
          ],
        },
      }
    }
    const { participants } = deserializedQueryString

    const adultAgeThreshold = sub(new Date(), { years: CHILDREN_MAX_AGE, days: 1 })
    const childrenAgeThreshold = sub(new Date(), { years: CHILDREN_MIN_AGE, days: 1 })

    const mappedParticipants: RoomsParticipantDistribution = []

    participants.forEach((room) => {
      const result: RoomOccupancy = {
        adults: 0,
        children: [],
        babies: [],
      }

      room.forEach((participant) => {
        const birthDate = new Date(participant)

        if (birthDate < adultAgeThreshold) {
          result.adults += 1
        }
        else if (birthDate < childrenAgeThreshold) {
          result.children.push(birthDate)
        }
        else {
          result.babies.push(birthDate)
        }
      })

      mappedParticipants.push(result)
    })

    return {
      filterSelection: {
        Participants: mappedParticipants,
      },
    }
  }
}

export const campaignsAndDiscountsMapper: DeserializedQueryStringToStateMapper = (deserializedQueryString: LowercaseKeys<DeserializedSearchQueryString>) => {
  if (!deserializedQueryString?.campaigns && !deserializedQueryString?.discount && !deserializedQueryString?.campaignsanddiscounts)
    return { filterSelection: {} }

  const { campaigns, discount, campaignsanddiscounts } = deserializedQueryString

  const selectedValues = [campaigns, discount, campaignsanddiscounts]
    .filter(Array.isArray)
    .flat()

  const filterSelectedValueUppercased = (selectedValues as string[]).map((option: string) => option.toUpperCase())

  return {
    filterSelection: {
      CampaignsAndDiscounts: filterSelectedValueUppercased,
    },
  }
}

export function createQueryStringToSearchStateMapperIgnoreCasing(dependencies: Partial<Dependencies> = {}) {
  const { defaultValues } = dependencies

  const {
    mappers = [
      paginationMapper,
      sortingMapper,
      accommodationIdsMapper,
      combineFilterSelectionMappers(
        identityFilterSelectionArrayMapper('AccommodationType'),
        identityFilterSelectionArrayMapper('Airline'),
        identityFilterSelectionArrayMapper('ChildrenFacilities'),
        identityFilterSelectionArrayMapper('DepartureAirport'),
        identityFilterSelectionArrayMapper('Facilities'),
        identityFilterSelectionArrayMapper('InternetFacilities'),
        identityFilterSelectionArrayMapper('Mealplan'),
        identityFilterSelectionArrayMapper('Themes'),
        identityFilterSelectionMapper('ArrivalAirport'),
        identityFilterSelectionMapper('City'),
        identityFilterSelectionMapper('Country'),
        identityFilterSelectionMapper('CustomersRating'),
        identityFilterSelectionMapper('MetersToBeach'),
        identityFilterSelectionMapper('MetersToCenter'),
        identityFilterSelectionMapper('Region'),
        identityFilterSelectionMapper('RoomCount'),
        identityFilterSelectionMapper('Stars'),
        identityFilterSelectionMapper('HasAvailability'),
        identityFilterSelectionMapper('HasAvailabilityNextSeason'),
        identityFilterSelectionMapper('MetersToSlope'),
        identityFilterSelectionMapper('MetersToSkiLift'),
        identityFilterSelectionMapper('SkiAreaMinimumAltitudeMeters'),
        identityFilterSelectionMapper('BedsInLivingroom'),
        identityFilterSelectionMapper('BathroomCount'),
        identityFilterSelectionMapper('SkiAreaSlopeKms'),
        identityFilterSelectionMapper('IsFlightOnly'),
        durationMapper(defaultValues?.Duration as string[] | undefined),
        transportTypeMapper(defaultValues?.TransportType as string | 'Flight'),
        lastMinuteMapper,
        collectionMapper,
        departureDateMapper,
        pricePerPersonMapper,
        participantsMapper(defaultValues?.Participants as RoomsParticipantDistribution | undefined),
        campaignsAndDiscountsMapper,
      ),
    ],
    queryStringSerializer = createQueryStringSerializer(),
  } = dependencies

  function map(queryString: string): Partial<SearchState> {
    const deserializedQueryString = queryStringSerializer.deserialize(queryString)
    const passThroughParams = getPassThroughParams(new URLSearchParams(queryString), searchPassThroughParams)
    const deserializedQueryStringLowercased = lowercaseKeys((deserializedQueryString as Record<string, string | string[]>))
    return mappers.reduce(
      (acc, mapper) => ({
        ...acc,
        ...mapper(deserializedQueryStringLowercased, passThroughParams),
      }),
      {},
    )
  }

  return { map }
}

export function isMultiSelectFilterKey(key: string): key is keyof SearchMultiSelectFilters {
  return MultiSelectKeys.includes(key as keyof SearchMultiSelectFilters)
}
